import Decimal from 'decimal.js'

import { ScaleLevel, ScaleLevelStatus, ScaleState } from '@/types/generated/algo'
import { Denomination } from '@/types/generated/common/trading'

export type ScaleLevelTone = 'positive' | 'negative' | 'active' | 'muted'

/** Number format for Scale prices and sizes: any venue's tick/lot, no zero padding. */
export const PRECISE = { maximumFractionDigits: 8 }

export function scaleLevelStatusLabel(status: ScaleLevelStatus): string {
  switch (status) {
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_PENDING:
      return 'Pending'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_WORKING:
      return 'Working'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_FILLED:
      return 'Filled'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_CANCELLED:
      return 'Cancelled'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_REJECTED:
      return 'Rejected'
    default:
      return 'Unknown'
  }
}

export function scaleLevelTone(status: ScaleLevelStatus): ScaleLevelTone {
  switch (status) {
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_FILLED:
      return 'positive'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_REJECTED:
      return 'negative'
    case ScaleLevelStatus.SCALE_LEVEL_STATUS_WORKING:
      return 'active'
    default:
      return 'muted'
  }
}

function toDecimal(value: string | undefined): Decimal | null {
  try {
    const d = new Decimal(value || '0')
    return d.isFinite() ? d : null
  } catch {
    return null
  }
}

/** executedQuantity / params.quantity (both in params.denomination), clamped to [0, 1]. */
export function scaleProgressFraction(scale: ScaleState): number {
  const total = toDecimal(scale.params?.quantity)
  const executed = toDecimal(scale.executedQuantity)
  if (!total || !executed || total.lte(0)) return 0
  return Decimal.min(1, Decimal.max(0, executed.dividedBy(total))).toNumber()
}

/**
 * Base-weighted average of the levels' prices — each level fills at its own limit, so
 * this is the run's true average fill price. Null until something has filled.
 *
 * `filledQuantity` is in the run's `denomination`: on a quote run it is a notional, so
 * the base bought at a level is `filled / price` and the average is Σquote / Σbase.
 */
export function scaleAvgFillPrice(
  levels: readonly ScaleLevel[],
  denomination: Denomination
): string | null {
  const quoteSized = denomination === Denomination.DENOMINATION_QUOTE
  let notional = new Decimal(0)
  let base = new Decimal(0)
  for (const level of levels) {
    if (!level.filledQuantity || level.filledQuantity === '0') continue
    const qty = toDecimal(level.filledQuantity)
    const price = toDecimal(level.price)
    if (!qty || !price || qty.lte(0) || price.lte(0)) continue
    notional = notional.plus(quoteSized ? qty : qty.times(price))
    base = base.plus(quoteSized ? qty.dividedBy(price) : qty)
  }
  return base.gt(0) ? notional.dividedBy(base).toString() : null
}

/** Levels in ladder order, index 0 (nearest the touch) first. */
export function orderedScaleLevels(levels: readonly ScaleLevel[]): ScaleLevel[] {
  return [...levels].sort((a, b) => a.index - b.index)
}
